import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, ofType, createEffect } from '@ngrx/effects';
import { combineLatest, EMPTY, of } from 'rxjs';
import { catchError, map, exhaustMap, take, switchMap } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';

import { profileOrUndefinedIfNotLoggedIn } from '@fcom/core/selectors';
import { isAuthenticatedPlusMember } from '@fcom/one-upgrade/utils/profile.utils';

import { Stage, UpgradeError } from '../../interfaces';
import { OffersService } from '../../services/offers.service';
import { AuthActions, StatusActions } from '../actions';
import { getBasketPassengerIds, getBookingRef } from '../selectors';

@Injectable()
export class AuthEffects {
  constructor(
    private offersService: OffersService,
    private store$: Store,
    private readonly actions$: Actions<AuthActions.AuthActionsUnion>,
    private location: Location,
    private router: Router
  ) {}

  loadPnr$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.loadPNR.type),
        exhaustMap(() => {
          return combineLatest([
            this.store$.pipe(profileOrUndefinedIfNotLoggedIn()),
            this.store$.pipe(select(getBookingRef)),
            this.store$.pipe(select(getBasketPassengerIds)),
          ]).pipe(
            take(1),
            switchMap(([profile, booking, passengers]) => {
              if (!booking?.lastName || !booking?.recLoc) {
                this.store$.dispatch(StatusActions.setStage({ stage: Stage.LOGIN }));
                return of(null);
              }

              this.store$.dispatch(StatusActions.setStage({ stage: Stage.LOADING }));
              const shouldUseMemberEndpoint = isAuthenticatedPlusMember(profile);

              return this.offersService.getOffers(booking, profile, shouldUseMemberEndpoint).pipe(
                map((response) => {
                  this.store$.dispatch(AuthActions.setUpgradeData(response));

                  // When pressing the browser back, do not go to loading, go to login.
                  this.location.replaceState(this.router.url.replace('/loading', ''));

                  // When the user logs in mid flow then there may be passengers already in the basket
                  this.store$.dispatch(
                    StatusActions.setStage({ stage: passengers.length ? Stage.FLIGHTS : Stage.PASSENGERS })
                  );
                }),
                catchError((e: unknown) => {
                  const error = UpgradeError[e as UpgradeError] || UpgradeError.httpError;

                  this.store$.dispatch(StatusActions.setError({ error }));
                  this.store$.dispatch(
                    StatusActions.setStage({ stage: error === UpgradeError.httpError ? Stage.LOGIN : Stage.ERROR })
                  );

                  return of(EMPTY);
                })
              );
            })
          );
        })
      );
    },
    {
      dispatch: false,
    }
  );
}
